/**
 * AdminCardEditModal — admin-only inline editor used from CardDetailModal.
 *
 * Wraps the writable card fields (per WRITABLE_PATCH_FIELDS on the backend),
 * Cardmarket URL, CardTrader blueprint mappings, and image management
 * (redownload from CardTrader / download from URL) in a single side drawer.
 *
 * Quality gates honoured here:
 *  - All mutations route through src/services/api.ts (no raw fetch)
 *  - Visibility is controlled by the parent — caller must already gate on
 *    useAuth().user?.is_admin === true
 *  - Numeric inputs are clamped client-side; backend re-validates regardless
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useQueryClient } from '@tanstack/react-query'
import { Dialog, Transition } from '@headlessui/react'
import { adminCardsApi, cardtraderApi, setsApi } from '@/services/api'
import { parseManuallyEditedFields, type AdminCardDetail, type AdminCardRow } from '@/types/admin'
import { proxyImageUrl } from '@/utils/imageLoader'
import ErrataPairView from './ErrataPairView'
import {
  X,
  Save,
  RefreshCw,
  Download,
  Link2,
  Unlink,
  Loader2,
  Image as ImageIcon,
  AlertTriangle,
  DollarSign,
  Sparkles,
  Search,
} from 'lucide-react'

interface Props {
  cardId: string
  open: boolean
  onClose: () => void
  /**
   * Optional callback fired after a successful save (PATCH, cardmarket URL,
   * blueprint link/unlink, or image change). The parent should refresh its
   * card data + react-query caches.
   */
  onChanged?: () => void
}

interface FieldDef {
  key: string
  label: string
  type?: 'text' | 'number' | 'textarea' | 'select' | 'checkbox'
  options?: string[]
  min?: number
  max?: number
  hint?: string
}

const FIELDS: FieldDef[] = [
  { key: 'name', label: 'Name' },
  { key: 'rarity', label: 'Rarity' },
  {
    key: 'art_type',
    label: 'Art Type',
    type: 'select',
    options: ['', 'SP', 'AA', 'TR', 'M'],
  },
  { key: 'sub_art_type', label: 'Sub Art Type' },
  { key: 'image_url', label: 'Image URL', hint: 'Use the image panel below to upload/cache' },
  { key: 'is_manga', label: 'Manga indicator', type: 'checkbox' },
  { key: 'type', label: 'Type' },
  { key: 'card_type', label: 'Card Type' },
  { key: 'color', label: 'Color', hint: 'Comma-separated, e.g. "Red,Green"' },
  { key: 'attributes', label: 'Attributes (raw JSON or string)', type: 'textarea' },
  { key: 'cost', label: 'Cost', type: 'number', min: 0, max: 99 },
  { key: 'power', label: 'Power', type: 'number', min: 0, max: 99999 },
  { key: 'counter', label: 'Counter', type: 'number', min: 0, max: 99999 },
  { key: 'life', label: 'Life', type: 'number', min: 0, max: 10 },
  { key: 'effect', label: 'Effect', type: 'textarea' },
  { key: 'trigger', label: 'Trigger', type: 'textarea' },
]

/**
 * Variants section — paired with art_type so admins see both axes (art treatment
 * vs foil dimension) on the same surface. Lives outside FIELDS because it
 * needs to render alongside the read-only art_type echo.
 */
const VARIANT_FIELDS: FieldDef[] = [
  {
    key: 'foil_variant',
    label: 'Foil',
    type: 'select',
    options: ['regular', 'foil', 'gold_foil'],
    hint: 'Card-level foil dimension (Phase 2 schema). DON!! cards may have foil-only or gold-foil-only printings.',
  },
]

/**
 * Promo section — only relevant when the card lives in a promo_virtual set.
 * Free-text style enum.
 */
const PROMO_SUBTYPE_OPTIONS = [
  '',
  'regional',
  'world',
  'online',
  'offline',
  'participation',
  'event',
  'pack_insert',
  'other',
]

function asInputValue(raw: unknown): string {
  if (raw === null || raw === undefined) return ''
  return String(raw)
}

/**
 * Many legacy cards stored their gameplay fields inside a single
 * `attributes` JSON blob (color, ability, trigger, counter, family, …)
 * instead of populating the dedicated columns. When a flat column is
 * empty, fall back to the value parsed from that JSON so admins see
 * the real data and can edit it back into the flat columns.
 */
function buildDraftFromCard(card: any): Record<string, string> {
  let parsed: Record<string, any> = {}
  const raw = card?.attributes
  if (typeof raw === 'string' && raw.trim().startsWith('{')) {
    try {
      parsed = JSON.parse(raw)
    } catch {
      parsed = {}
    }
  } else if (raw && typeof raw === 'object') {
    parsed = raw
  }

  const fallback: Record<string, any> = {
    color: parsed.color,
    effect: parsed.ability ?? parsed.effect,
    trigger: parsed.trigger,
    counter: parsed.counter,
    life: parsed.life,
    card_type: parsed.card_type ?? parsed.cardType,
    type: parsed.type,
  }

  const draft: Record<string, string> = {}
  const allFields = [...FIELDS, ...VARIANT_FIELDS]
  for (const f of allFields) {
    const flat = (card as any)[f.key]
    const flatEmpty =
      flat === null || flat === undefined || flat === '' || flat === '-'
    const fbVal = fallback[f.key]
    const useFallback =
      flatEmpty && fbVal !== undefined && fbVal !== null && fbVal !== '' && fbVal !== '-'
    if (f.key === 'attributes') {
      // Always show raw attributes blob as-is for transparency.
      draft[f.key] = asInputValue(flat)
      continue
    }
    let value: unknown = useFallback ? fbVal : flat
    // Number fields: coerce to numeric string; reject sentinel '-' / non-numeric.
    if (f.type === 'number') {
      const n = Number(value)
      value = Number.isFinite(n) && value !== '' && value !== null && value !== undefined ? n : ''
    }
    if (f.key === 'foil_variant' && (value === '' || value === null || value === undefined)) {
      // Default to 'regular' so the select always reflects a valid value.
      value = 'regular'
    }
    draft[f.key] = asInputValue(value)
  }
  // Promo subtype is rendered conditionally but always populated in the draft.
  draft['promo_subtype'] = asInputValue((card as any)?.promo_subtype ?? '')
  return draft
}

export default function AdminCardEditModal({ cardId, open, onClose, onChanged }: Props) {
  const queryClient = useQueryClient()
  const [detail, setDetail] = useState<AdminCardDetail | null>(null)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [initialDraft, setInitialDraft] = useState<Record<string, string>>({})
  const [cmUrl, setCmUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [redownloading, setRedownloading] = useState(false)
  const [downloadingFromUrl, setDownloadingFromUrl] = useState(false)
  const [showUrlInput, setShowUrlInput] = useState(false)
  const [imageUrlInput, setImageUrlInput] = useState('')
  const [unlinkingId, setUnlinkingId] = useState<number | null>(null)
  const [prices, setPrices] = useState<{
    ct: { min: number; avg: number; median: number; currency: string; last_synced_at?: string } | null
    cm: { min: number; avg: number; trend: number } | null
  }>({ ct: null, cm: null })
  const [priceLoading, setPriceLoading] = useState(false)
  const [syncingPrice, setSyncingPrice] = useState(false)

  // Errata section state — these write through dedicated mark-errata /
  // clear-errata endpoints rather than the generic PATCH so the audit log
  // and mask extension match what the backend expects.
  const [isErrata, setIsErrata] = useState(false)
  const [initialIsErrata, setInitialIsErrata] = useState(false)
  const [errataOf, setErrataOf] = useState('')
  const [initialErrataOf, setInitialErrataOf] = useState('')
  const [errataReason, setErrataReason] = useState('')
  const [initialErrataReason, setInitialErrataReason] = useState('')
  const [errataPublishedAt, setErrataPublishedAt] = useState('')
  const [initialErrataPublishedAt, setInitialErrataPublishedAt] = useState('')
  // errata_of autocomplete state.
  const [errataOfHits, setErrataOfHits] = useState<AdminCardRow[]>([])
  const [errataOfSearching, setErrataOfSearching] = useState(false)
  const errataOfSearchTimer = useRef<number | null>(null)

  // Promo subtype is conditional on the card's set_type. We fetch the set so
  // we can detect promo_virtual without a backend join change.
  const [setType, setSetType] = useState<string | null>(null)

  const fetchPrices = async () => {
    setPriceLoading(true)
    try {
      const res: any = await cardtraderApi.getCardPrice(cardId)
      const p = res?.price ?? null
      const cm = p?.cardmarket ?? null
      setPrices({
        ct: p
          ? {
              min: Number(p.min_price ?? p.min ?? 0),
              avg: Number(p.avg_price ?? p.avg ?? 0),
              median: Number(p.median_price ?? p.median ?? 0),
              currency: p.currency ?? 'EUR',
              last_synced_at: p.last_synced_at,
            }
          : null,
        cm: cm
          ? {
              min: Number(cm.min_price ?? cm.min ?? 0),
              avg: Number(cm.avg_price ?? cm.avg ?? 0),
              trend: Number(cm.median_price ?? cm.trend ?? 0),
            }
          : null,
      })
    } catch {
      setPrices({ ct: null, cm: null })
    } finally {
      setPriceLoading(false)
    }
  }

  const onResyncPrice = async () => {
    setSyncingPrice(true)
    try {
      await cardtraderApi.syncCardPrice(cardId)
      toast.success('CardTrader price re-synced')
      await fetchPrices()
      invalidateCardCaches()
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? err.message ?? 'Resync failed'
      toast.error(msg)
    } finally {
      setSyncingPrice(false)
    }
  }

  const refresh = async () => {
    setLoading(true)
    setError(null)
    try {
      const d = await adminCardsApi.get(cardId)
      setDetail(d)
      const init = buildDraftFromCard(d.card)
      setDraft(init)
      setInitialDraft(init)
      setCmUrl(d.card.cardmarket_url ?? '')

      // Errata state — initial flags + reason + published_at.
      const cardErrata = !!(d.card as any).is_errata
      const cardErrataOf = (d.card as any).errata_of ?? ''
      const cardErrataReason = (d.card as any).errata_reason ?? ''
      const cardErrataPublishedAt = (d.card as any).errata_published_at ?? ''
      setIsErrata(cardErrata)
      setInitialIsErrata(cardErrata)
      setErrataOf(cardErrataOf)
      setInitialErrataOf(cardErrataOf)
      setErrataReason(cardErrataReason)
      setInitialErrataReason(cardErrataReason)
      setErrataPublishedAt(cardErrataPublishedAt)
      setInitialErrataPublishedAt(cardErrataPublishedAt)

      // Fetch the parent set so we know whether to show promo_subtype.
      // Best-effort; not fatal on failure.
      const setId = (d.card as any).set_id
      if (setId) {
        try {
          const set: any = await setsApi.getById(setId)
          setSetType(set?.set_type ?? null)
        } catch {
          setSetType(null)
        }
      } else {
        setSetType(null)
      }
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err.message ?? 'Failed to load card')
    } finally {
      setLoading(false)
    }
  }

  // Search admin cards by name/id for the errata_of autocomplete. Debounced.
  const onErrataOfQuery = (q: string) => {
    setErrataOf(q)
    if (errataOfSearchTimer.current !== null) {
      window.clearTimeout(errataOfSearchTimer.current)
    }
    if (!q.trim()) {
      setErrataOfHits([])
      return
    }
    errataOfSearchTimer.current = window.setTimeout(async () => {
      setErrataOfSearching(true)
      try {
        const res = await adminCardsApi.list({ q, limit: 8 })
        // Exclude the current card from matches so admins can't link to self.
        setErrataOfHits(res.cards.filter((c) => c.id !== cardId))
      } catch {
        setErrataOfHits([])
      } finally {
        setErrataOfSearching(false)
      }
    }, 250)
  }

  useEffect(() => {
    if (!open || !cardId) return
    refresh()
    fetchPrices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cardId])

  const editedMask = useMemo(
    () => parseManuallyEditedFields(detail?.card.manually_edited_fields),
    [detail]
  )

  // Visibility is controlled by the Headless UI <Dialog open=...> at render
  // time. We deliberately do NOT early-return on `!open` — that bypasses the
  // dialog stack-machine and would re-introduce the bug where the parent
  // <Dialog> (CardDetailModal) sees an unstacked nested element and closes
  // itself on outside-click.

  const invalidateCardCaches = () => {
    queryClient.invalidateQueries({ queryKey: ['card', cardId] })
    queryClient.invalidateQueries({ queryKey: ['allCards'] })
    queryClient.invalidateQueries({ queryKey: ['cards'] })
    queryClient.invalidateQueries({ queryKey: ['card-quantities', cardId] })
    queryClient.invalidateQueries({ queryKey: ['admin-card', cardId] })
    onChanged?.()
  }

  const save = async () => {
    if (!detail) return
    setSaving(true)
    setError(null)
    try {
      const payload: Record<string, any> = {}
      // FIELDS + VARIANT_FIELDS share the same diff-against-initialDraft
      // approach. Promo subtype is appended below.
      const writableFields = [...FIELDS, ...VARIANT_FIELDS]
      for (const f of writableFields) {
        // Compare against the initial draft snapshot (which already reflects
        // the attributes-JSON fallback) rather than the raw flat column —
        // otherwise unchanged fields look "changed" because the flat column
        // is empty while the visible value came from the JSON blob, and the
        // backend would mark them as manually-edited despite no edit.
        const before = initialDraft[f.key] ?? ''
        const next = draft[f.key] ?? ''
        if (before === next) continue
        if (f.type === 'number') {
          const trimmed = next.trim()
          if (trimmed === '' || trimmed === '-') {
            payload[f.key] = null
          } else {
            const n = Number(trimmed)
            if (!Number.isFinite(n)) {
              setError(`${f.label} must be a number or empty`)
              setSaving(false)
              return
            }
            if (typeof f.min === 'number' && n < f.min) {
              setError(`${f.label} must be ≥ ${f.min}`)
              setSaving(false)
              return
            }
            if (typeof f.max === 'number' && n > f.max) {
              setError(`${f.label} must be ≤ ${f.max}`)
              setSaving(false)
              return
            }
            payload[f.key] = n
          }
        } else if (f.type === 'checkbox') {
          payload[f.key] = next === 'true' || next === '1' ? 1 : 0
        } else {
          payload[f.key] = next === '' ? null : next
        }
      }

      // Promo subtype — only diffed/sent when the card's set is promo_virtual.
      if (setType === 'promo_virtual') {
        const before = initialDraft.promo_subtype ?? ''
        const next = draft.promo_subtype ?? ''
        if (before !== next) {
          payload.promo_subtype = next === '' ? null : next
        }
      }

      let didChange = false
      if (Object.keys(payload).length > 0) {
        await adminCardsApi.update(cardId, payload)
        didChange = true
      }
      if (cmUrl !== (detail.card.cardmarket_url ?? '')) {
        await adminCardsApi.setCardmarketUrl(cardId, cmUrl || null)
        didChange = true
      }

      // Errata changes flow through dedicated endpoints. We diff against the
      // initial snapshot to detect intent.
      const errataChanged =
        isErrata !== initialIsErrata ||
        (isErrata && errataOf !== initialErrataOf) ||
        (isErrata && errataReason !== initialErrataReason) ||
        (isErrata && errataPublishedAt !== initialErrataPublishedAt)
      if (errataChanged) {
        if (!isErrata && initialIsErrata) {
          // Toggling off → clear-errata.
          await adminCardsApi.clearErrata(cardId)
          didChange = true
        } else if (isErrata) {
          if (!errataOf.trim()) {
            setError('errata_of is required when "Is errata" is checked.')
            setSaving(false)
            return
          }
          await adminCardsApi.markErrata(cardId, {
            errata_of: errataOf.trim(),
            errata_reason: errataReason.trim() || undefined,
            errata_published_at: errataPublishedAt.trim() || null,
          })
          didChange = true
        }
      }

      if (!didChange) {
        toast('No changes to save')
      } else {
        toast.success('Card updated')
        invalidateCardCaches()
        await refresh()
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? err.message ?? 'Save failed'
      setError(msg)
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  const onRedownload = async () => {
    setRedownloading(true)
    setError(null)
    try {
      const result = await adminCardsApi.redownloadImage(cardId)
      toast.success(`Image cached (${Math.round(result.bytes / 1024)} KB)`)
      invalidateCardCaches()
      await refresh()
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? err.message ?? 'Redownload failed'
      setError(msg)
      toast.error(msg)
    } finally {
      setRedownloading(false)
    }
  }

  const onDownloadFromUrl = async () => {
    if (!imageUrlInput.trim()) return
    setDownloadingFromUrl(true)
    setError(null)
    try {
      const result = await adminCardsApi.downloadImageFromUrl(cardId, imageUrlInput.trim())
      toast.success(`Image cached (${Math.round(result.bytes / 1024)} KB)`)
      setImageUrlInput('')
      setShowUrlInput(false)
      invalidateCardCaches()
      await refresh()
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? err.message ?? 'Download failed'
      setError(msg)
      toast.error(msg)
    } finally {
      setDownloadingFromUrl(false)
    }
  }

  const onUnlinkBlueprint = async (blueprintId: number) => {
    setUnlinkingId(blueprintId)
    try {
      await adminCardsApi.unlinkBlueprint(cardId, blueprintId)
      toast.success('Blueprint unlinked')
      invalidateCardCaches()
      await refresh()
    } catch (err: any) {
      toast.error(err?.response?.data?.error ?? err.message ?? 'Unlink failed')
    } finally {
      setUnlinkingId(null)
    }
  }

  const card = detail?.card
  const currentImageUrl = card?.image_url ?? null

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[60]"
        onClose={onClose}
        aria-labelledby="admin-card-edit-title"
      >
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        </Transition.Child>

        {/* Slide-in drawer (right-aligned, max-w-2xl) */}
        <div
          className="fixed inset-0 flex justify-end"
          data-testid="admin-card-edit-modal"
        >
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-out duration-200"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in duration-150"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel className="w-full max-w-2xl bg-white dark:bg-dark-surface h-full overflow-auto shadow-xl flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border px-4 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <h2
              id="admin-card-edit-title"
              className="text-lg font-bold text-gray-900 dark:text-dark-text truncate"
            >
              Admin edit · {cardId}
            </h2>
            {card?.name && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{card.name}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-dark-border rounded"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="mx-4 mt-3 p-3 rounded bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {loading && (
          <div className="p-6 flex items-center justify-center text-gray-500 dark:text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading card data…
          </div>
        )}

        {!loading && detail && (
          <div className="flex-1 px-4 py-4 space-y-6">
            {/* Image management */}
            <section
              className="border border-gray-200 dark:border-dark-border rounded-lg p-3 bg-gray-50 dark:bg-dark-bg"
              data-testid="admin-card-image-panel"
            >
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-1 text-gray-800 dark:text-dark-text">
                <ImageIcon className="h-4 w-4" /> Image
              </h3>
              <div className="flex items-start gap-3">
                <div className="w-24 flex-shrink-0">
                  {currentImageUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={proxyImageUrl(currentImageUrl)}
                      alt={card?.name ?? cardId}
                      className="w-full rounded shadow"
                      onError={(e) => {
                        const img = e.currentTarget as HTMLImageElement
                        img.style.display = 'none'
                      }}
                    />
                  ) : (
                    <div className="w-full aspect-[3/4] flex items-center justify-center bg-gray-200 dark:bg-dark-border text-gray-400 text-xs rounded">
                      No image
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-2 text-sm">
                  <p className="text-xs font-mono break-all text-gray-600 dark:text-gray-300">
                    {currentImageUrl || '(unset)'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={onRedownload}
                      disabled={redownloading}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-600 text-white text-xs hover:bg-blue-700 disabled:opacity-60"
                      data-testid="admin-image-redownload"
                    >
                      {redownloading ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3 w-3" />
                      )}
                      Redownload from CardTrader
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowUrlInput(v => !v)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-300 dark:border-dark-border text-xs hover:bg-gray-100 dark:hover:bg-dark-border"
                      data-testid="admin-image-toggle-url-input"
                    >
                      <Download className="h-3 w-3" />
                      Download from URL
                    </button>
                  </div>
                  {showUrlInput && (
                    <div className="flex flex-wrap gap-2 items-center">
                      <input
                        type="url"
                        value={imageUrlInput}
                        onChange={e => setImageUrlInput(e.target.value)}
                        placeholder="https://example.com/card.png"
                        className="flex-1 min-w-[200px] border border-gray-300 dark:border-dark-border rounded px-2 py-1 text-xs bg-white dark:bg-dark-surface"
                        data-testid="admin-image-url-input"
                      />
                      <button
                        type="button"
                        onClick={onDownloadFromUrl}
                        disabled={!imageUrlInput.trim() || downloadingFromUrl}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-600 text-white text-xs hover:bg-emerald-700 disabled:opacity-60"
                        data-testid="admin-image-download-url-submit"
                      >
                        {downloadingFromUrl ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Download className="h-3 w-3" />
                        )}
                        Fetch
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Identity (read-only) */}
            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-dark-text">
                Identity (read-only)
              </h3>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-gray-500 dark:text-gray-400 mb-0.5">Card ID</div>
                  <div className="font-mono text-gray-800 dark:text-dark-text break-all">
                    {(card as any)?.id ?? cardId}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 dark:text-gray-400 mb-0.5">Set</div>
                  <div className="font-mono text-gray-800 dark:text-dark-text break-all">
                    {(card as any)?.set_id ?? '—'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 dark:text-gray-400 mb-0.5">Number</div>
                  <div className="font-mono text-gray-800 dark:text-dark-text">
                    {(card as any)?.number ?? '—'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 dark:text-gray-400 mb-0.5">Last CT sync</div>
                  <div className="text-gray-800 dark:text-dark-text">
                    {(card as any)?.last_ct_sync_at ?? '—'}
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-gray-500 dark:text-gray-400">
                Card ID, Set, and Number are immutable identity columns. Use the Card Manager to merge or recreate.
              </p>
            </section>

            {/* Pricing */}
            <section
              className="border border-gray-200 dark:border-dark-border rounded-lg p-3 bg-gray-50 dark:bg-dark-bg"
              data-testid="admin-card-pricing-panel"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold flex items-center gap-1 text-gray-800 dark:text-dark-text">
                  <DollarSign className="h-4 w-4" /> Prices
                </h3>
                <button
                  type="button"
                  onClick={onResyncPrice}
                  disabled={syncingPrice}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded bg-purple-600 text-white text-xs hover:bg-purple-700 disabled:opacity-60"
                  data-testid="admin-card-resync-price"
                >
                  {syncingPrice ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3" />
                  )}
                  Resync from CardTrader
                </button>
              </div>
              {priceLoading ? (
                <div className="text-xs text-gray-500 dark:text-gray-400">Loading prices…</div>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-dark-border">
                      <th className="font-medium py-1">Source</th>
                      <th className="font-medium py-1 text-right">Min</th>
                      <th className="font-medium py-1 text-right">Avg</th>
                      <th className="font-medium py-1 text-right">Median / Trend</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                    <tr>
                      <td className="py-1 font-medium text-blue-700 dark:text-blue-300">CardTrader</td>
                      <td className="py-1 text-right text-green-700 dark:text-green-400">
                        {prices.ct ? `€${prices.ct.min.toFixed(2)}` : '—'}
                      </td>
                      <td className="py-1 text-right text-blue-700 dark:text-blue-300">
                        {prices.ct && prices.ct.avg > 0 ? `€${prices.ct.avg.toFixed(2)}` : '—'}
                      </td>
                      <td className="py-1 text-right text-purple-700 dark:text-purple-400">
                        {prices.ct && prices.ct.median > 0 ? `€${prices.ct.median.toFixed(2)}` : '—'}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-1 font-medium text-sky-700 dark:text-sky-300">Cardmarket</td>
                      <td className="py-1 text-right text-green-700 dark:text-green-400">
                        {prices.cm ? `€${prices.cm.min.toFixed(2)}` : '—'}
                      </td>
                      <td className="py-1 text-right text-blue-700 dark:text-blue-300">
                        {prices.cm && prices.cm.avg > 0 ? `€${prices.cm.avg.toFixed(2)}` : '—'}
                      </td>
                      <td className="py-1 text-right text-purple-700 dark:text-purple-400">
                        {prices.cm && prices.cm.trend > 0 ? `€${prices.cm.trend.toFixed(2)}` : '—'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
              {prices.ct?.last_synced_at && (
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                  CT last synced: {prices.ct.last_synced_at}
                </p>
              )}
            </section>

            {/* Field grid */}
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-dark-text">
                Card fields
              </h3>
              {FIELDS.map(f => {
                const isEdited = editedMask.has(f.key)
                return (
                  <div key={f.key} className="grid grid-cols-[140px_1fr] gap-3 items-start">
                    <label
                      className="pt-2 text-xs font-medium text-gray-700 dark:text-gray-300"
                      htmlFor={`admin-field-${f.key}`}
                    >
                      {f.label}
                      {isEdited && (
                        <span
                          className="ml-1 px-1 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold"
                          title="Manually edited — protected from sync"
                        >
                          M
                        </span>
                      )}
                    </label>
                    <div className="flex-1">
                      {f.type === 'select' ? (
                        <select
                          id={`admin-field-${f.key}`}
                          value={draft[f.key] ?? ''}
                          onChange={e => setDraft({ ...draft, [f.key]: e.target.value })}
                          className="w-full border border-gray-300 dark:border-dark-border rounded px-2 py-1 text-sm bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text"
                        >
                          {(f.options ?? []).map(opt => (
                            <option key={opt} value={opt}>
                              {opt || '(none)'}
                            </option>
                          ))}
                        </select>
                      ) : f.type === 'textarea' ? (
                        <textarea
                          id={`admin-field-${f.key}`}
                          value={draft[f.key] ?? ''}
                          onChange={e => setDraft({ ...draft, [f.key]: e.target.value })}
                          rows={3}
                          className="w-full border border-gray-300 dark:border-dark-border rounded px-2 py-1 text-sm bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text"
                        />
                      ) : f.type === 'checkbox' ? (
                        <label className="inline-flex items-center gap-2 text-sm">
                          <input
                            id={`admin-field-${f.key}`}
                            type="checkbox"
                            checked={
                              draft[f.key] === '1' ||
                              draft[f.key] === 'true' ||
                              draft[f.key] === 'on'
                            }
                            onChange={e =>
                              setDraft({ ...draft, [f.key]: e.target.checked ? '1' : '0' })
                            }
                            className="h-4 w-4"
                          />
                          <span className="text-gray-700 dark:text-gray-300">
                            {draft[f.key] === '1' ? 'Manga / promo' : 'Standard'}
                          </span>
                        </label>
                      ) : (
                        <input
                          id={`admin-field-${f.key}`}
                          type={f.type === 'number' ? 'number' : 'text'}
                          min={f.min}
                          max={f.max}
                          value={draft[f.key] ?? ''}
                          onChange={e => setDraft({ ...draft, [f.key]: e.target.value })}
                          className="w-full border border-gray-300 dark:border-dark-border rounded px-2 py-1 text-sm bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text"
                        />
                      )}
                      {f.hint && (
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                          {f.hint}
                        </p>
                      )}
                    </div>
                  </div>
                )
              })}
            </section>

            {/* Variants — paired with art_type for full variant context */}
            <section
              className="space-y-3 border-t border-gray-200 dark:border-dark-border pt-4"
              data-testid="admin-card-variants-section"
            >
              <h3 className="text-sm font-semibold text-gray-800 dark:text-dark-text flex items-center gap-1">
                <Sparkles className="h-4 w-4" /> Variants
              </h3>
              <div className="grid grid-cols-[140px_1fr] gap-3 items-start">
                <span className="pt-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                  Art Type
                </span>
                <span className="pt-2 text-xs font-mono text-gray-700 dark:text-gray-300">
                  {draft.art_type || '(regular)'}
                  <span className="ml-2 text-[10px] text-gray-500 dark:text-gray-400">
                    Edit above in Card fields
                  </span>
                </span>
              </div>
              {VARIANT_FIELDS.map((f) => {
                const isEdited = editedMask.has(f.key)
                return (
                  <div
                    key={f.key}
                    className="grid grid-cols-[140px_1fr] gap-3 items-start"
                    data-testid={`admin-variant-row-${f.key}`}
                  >
                    <label
                      className="pt-2 text-xs font-medium text-gray-700 dark:text-gray-300"
                      htmlFor={`admin-field-${f.key}`}
                    >
                      {f.label}
                      {isEdited && (
                        <span
                          className="ml-1 px-1 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold"
                          title="Manually edited — protected from sync"
                        >
                          M
                        </span>
                      )}
                    </label>
                    <div className="flex-1">
                      <select
                        id={`admin-field-${f.key}`}
                        value={draft[f.key] ?? 'regular'}
                        onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                        className="w-full border border-gray-300 dark:border-dark-border rounded px-2 py-1 text-sm bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text"
                        data-testid={`admin-field-${f.key}`}
                      >
                        {(f.options ?? []).map((opt) => (
                          <option key={opt} value={opt}>
                            {opt === 'regular'
                              ? 'Regular'
                              : opt === 'foil'
                                ? 'Foil'
                                : opt === 'gold_foil'
                                  ? 'Gold Foil'
                                  : opt}
                          </option>
                        ))}
                      </select>
                      {f.hint && (
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                          {f.hint}
                        </p>
                      )}
                    </div>
                  </div>
                )
              })}
            </section>

            {/* Errata — flag, reason, errata_of pair, published_at */}
            <section
              className="space-y-3 border-t border-gray-200 dark:border-dark-border pt-4"
              data-testid="admin-card-errata-section"
            >
              <h3 className="text-sm font-semibold text-gray-800 dark:text-dark-text flex items-center gap-1">
                <AlertTriangle className="h-4 w-4 text-amber-500" /> Errata
              </h3>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isErrata}
                  onChange={(e) => {
                    const v = e.target.checked
                    setIsErrata(v)
                    if (!v) {
                      // Clear local fields when unchecking — save will then
                      // POST clear-errata to wipe them server-side too.
                      setErrataOf('')
                      setErrataReason('')
                      setErrataPublishedAt('')
                    }
                  }}
                  className="h-4 w-4"
                  data-testid="admin-is-errata-checkbox"
                />
                <span className="text-gray-700 dark:text-gray-300">
                  This card is an errata variant of another card
                </span>
              </label>

              {isErrata && (
                <>
                  <div className="grid grid-cols-[140px_1fr] gap-3 items-start">
                    <label
                      htmlFor="admin-errata-of"
                      className="pt-2 text-xs font-medium text-gray-700 dark:text-gray-300"
                    >
                      Errata of (card id)
                    </label>
                    <div className="flex-1">
                      <div className="relative">
                        <input
                          id="admin-errata-of"
                          type="text"
                          value={errataOf}
                          onChange={(e) => onErrataOfQuery(e.target.value)}
                          placeholder="OP01-001"
                          className="w-full border border-gray-300 dark:border-dark-border rounded px-2 py-1 text-sm bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text"
                          data-testid="admin-errata-of-input"
                          autoComplete="off"
                        />
                        {errataOfSearching && (
                          <Loader2 className="h-3 w-3 animate-spin absolute right-2 top-2 text-gray-400" />
                        )}
                        {errataOfHits.length > 0 && (
                          <ul
                            className="absolute z-10 mt-1 w-full max-h-48 overflow-auto bg-white dark:bg-dark-surface border border-gray-300 dark:border-dark-border rounded shadow-lg text-xs"
                            data-testid="admin-errata-of-suggestions"
                          >
                            {errataOfHits.map((hit) => (
                              <li
                                key={hit.id}
                                className="px-2 py-1 hover:bg-blue-50 dark:hover:bg-dark-border cursor-pointer flex items-center gap-2"
                                onClick={() => {
                                  setErrataOf(hit.id)
                                  setErrataOfHits([])
                                }}
                              >
                                <span className="font-mono">{hit.id}</span>
                                <span className="text-gray-600 dark:text-gray-400 truncate">
                                  {hit.name ?? '—'}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                        The id of the corrected (canonical) card this errata
                        replaces. Type to search.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-[140px_1fr] gap-3 items-start">
                    <label
                      htmlFor="admin-errata-reason"
                      className="pt-2 text-xs font-medium text-gray-700 dark:text-gray-300"
                    >
                      Reason
                    </label>
                    <textarea
                      id="admin-errata-reason"
                      value={errataReason}
                      onChange={(e) => setErrataReason(e.target.value)}
                      rows={2}
                      placeholder="e.g. Corrected counter from 1000 to 2000."
                      className="w-full border border-gray-300 dark:border-dark-border rounded px-2 py-1 text-sm bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text"
                      data-testid="admin-errata-reason-input"
                    />
                  </div>

                  <div className="grid grid-cols-[140px_1fr] gap-3 items-start">
                    <label
                      htmlFor="admin-errata-published-at"
                      className="pt-2 text-xs font-medium text-gray-700 dark:text-gray-300"
                    >
                      Published at
                    </label>
                    <input
                      id="admin-errata-published-at"
                      type="date"
                      value={errataPublishedAt}
                      onChange={(e) => setErrataPublishedAt(e.target.value)}
                      className="w-full border border-gray-300 dark:border-dark-border rounded px-2 py-1 text-sm bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text"
                      data-testid="admin-errata-published-at-input"
                    />
                  </div>

                  {errataOf && errataOf === initialErrataOf && initialIsErrata && (
                    <div data-testid="admin-errata-pair-preview">
                      <ErrataPairView cardId={cardId} compact />
                    </div>
                  )}
                </>
              )}
            </section>

            {/* Promo — only for cards in promo_virtual sets */}
            {setType === 'promo_virtual' && (
              <section
                className="space-y-3 border-t border-gray-200 dark:border-dark-border pt-4"
                data-testid="admin-card-promo-section"
              >
                <h3 className="text-sm font-semibold text-gray-800 dark:text-dark-text flex items-center gap-1">
                  <Search className="h-4 w-4" /> Promo metadata
                </h3>
                <div className="grid grid-cols-[140px_1fr] gap-3 items-start">
                  <label
                    htmlFor="admin-promo-subtype"
                    className="pt-2 text-xs font-medium text-gray-700 dark:text-gray-300"
                  >
                    Promo subtype
                    {editedMask.has('promo_subtype') && (
                      <span
                        className="ml-1 px-1 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold"
                        title="Manually edited — protected from sync"
                      >
                        M
                      </span>
                    )}
                  </label>
                  <select
                    id="admin-promo-subtype"
                    value={draft.promo_subtype ?? ''}
                    onChange={(e) => setDraft({ ...draft, promo_subtype: e.target.value })}
                    className="w-full border border-gray-300 dark:border-dark-border rounded px-2 py-1 text-sm bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text"
                    data-testid="admin-promo-subtype-select"
                  >
                    {PROMO_SUBTYPE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt === '' ? '(none)' : opt}
                      </option>
                    ))}
                  </select>
                </div>
              </section>
            )}

            {/* Cardmarket URL */}
            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-dark-text">
                Cardmarket URL
              </h3>
              <div className="flex gap-2 items-start">
                <input
                  type="url"
                  value={cmUrl}
                  onChange={e => setCmUrl(e.target.value)}
                  placeholder="https://www.cardmarket.com/…"
                  className="flex-1 border border-gray-300 dark:border-dark-border rounded px-2 py-1 text-sm bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text"
                  data-testid="admin-cmurl-input"
                />
                {editedMask.has('cardmarket_url') && (
                  <span
                    className="px-2 py-1 rounded bg-amber-100 text-amber-800 text-xs font-semibold"
                    title="Manually edited"
                  >
                    Manual
                  </span>
                )}
              </div>
            </section>

            {/* Blueprint mappings */}
            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-dark-text flex items-center gap-1">
                <Link2 className="h-4 w-4" /> CardTrader blueprints
              </h3>
              {detail.mappings.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  No CardTrader blueprints linked. Use the admin Card Manager to link one.
                </p>
              ) : (
                <ul className="space-y-1">
                  {detail.mappings.map(m => (
                    <li
                      key={m.id}
                      className="flex items-center justify-between gap-2 text-xs border border-gray-200 dark:border-dark-border rounded px-2 py-1"
                    >
                      <div className="min-w-0">
                        <div className="font-mono">
                          BP {m.blueprint_id}
                          {m.language ? ` · ${m.language}` : ''}
                        </div>
                        <div className="text-gray-500 dark:text-gray-400 truncate">
                          {m.blueprint_name ?? '(no name)'} · {m.mapping_method}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => onUnlinkBlueprint(m.blueprint_id)}
                        disabled={unlinkingId === m.blueprint_id}
                        className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 disabled:opacity-60"
                        data-testid={`admin-unlink-blueprint-${m.blueprint_id}`}
                      >
                        {unlinkingId === m.blueprint_id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Unlink className="h-3 w-3" />
                        )}
                        Unlink
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-[10px] text-gray-500 dark:text-gray-400">
                To link new blueprints, open the admin Card Manager and use the conflict / unmapped
                workbench.
              </p>
            </section>
          </div>
        )}

        {/* Footer */}
        {!loading && detail && (
          <div className="sticky bottom-0 bg-gray-50 dark:bg-dark-bg border-t border-gray-200 dark:border-dark-border px-4 py-3 flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-border rounded hover:bg-gray-100 dark:hover:bg-dark-border"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
              data-testid="admin-card-edit-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </button>
          </div>
        )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
